import type { ExperimentsMonitorManager, ExperimentMonitorStatsRow } from '@/state/experiments/useExperimentsMonitor';
import { Fragment, useState } from 'react';
import { EmptyState, Pager, Panel, TableScroll } from '../ConsolePanel';
import { ExperimentRow, ExperimentRowHead, rowMinWidth } from './ExperimentRow';

export type GroupedExperimentsTableProps = {
  manager: ExperimentsMonitorManager;
};

export const GroupedExperimentsTable = ({ manager }: GroupedExperimentsTableProps) => {
  const { filteredExperiments, activeCount, search, scopeTab } = manager;
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 5;

  // Group experiments by website
  const groupedByWebsite = (() => {
    const grouped: Record<string, ExperimentMonitorStatsRow[]> = {};
    for (const exp of filteredExperiments) {
      const key = exp.websiteUrl || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(exp);
    }
    return grouped;
  })();

  const websiteEntries = Object.entries(groupedByWebsite);
  const totalPages = Math.max(1, Math.ceil(websiteEntries.length / perPage));
  const paginatedEntries = websiteEntries.slice((currentPage - 1) * perPage, currentPage * perPage);

  const toggleRow = (rowId: string) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  /*
    `filteredExperiments` has already been through the scope pills and the search box,
    so "No running experiments found." was a claim about the whole account made from a
    filtered set: typing a query, or picking "Only mine", said nothing was running.

    The scope narrows before the search does, so a scope that excludes everything makes
    the query irrelevant — hence the search case is only reachable with something in
    scope to have excluded.
  */
  const emptyReason = (() => {
    if (activeCount > 0 && search) {
      return `Nothing matches "${search}" — ${activeCount} experiment${activeCount === 1 ? '' : 's'} in this scope.`;
    }
    if (scopeTab === 'mine') return 'No experiments were created or started by you.';
    if (scopeTab === 'account') return 'No experiments in the account you selected.';
    return 'No experiments have reported any data yet.';
  })();

  if (filteredExperiments.length === 0) {
    return (
      <Panel
        title="All Experiments (Grouped by Website)"
        description="Overview of all experiments, organized by website."
        tourAnchor="performance-all"
      >
        <EmptyState>{emptyReason}</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel
      title="All Experiments (Grouped by Website)"
      description="Overview of all experiments, organized by website."
      bodyClassName="p-0"
      tourAnchor="performance-all"
    >
      <TableScroll minWidth={rowMinWidth(false)}>
        <ExperimentRowHead showDomain={false} />

        {paginatedEntries.map(([websiteUrl, exps]) => (
          <Fragment key={websiteUrl}>
            {/*
              Not `micro-label`. That utility uppercases and tracks out at 0.14em,
              which is right for a column head and wrong for a hostname: it destroys
              the case of path segments and makes a long URL harder to scan, and this
              heading is the only thing saying which site the rows below belong to.
            */}
            <div
              className="bg-panel2 border-line text-dim truncate border-b px-3.5 py-1.5 font-mono text-micro"
              title={websiteUrl}
            >
              {websiteUrl}
            </div>

            {exps.map((exp) => (
              <ExperimentRow
                key={exp.id}
                experiment={exp}
                isExpanded={expandedRowId === `grouped-${exp.id}`}
                onToggle={() => toggleRow(`grouped-${exp.id}`)}
                showDomain={false}
              />
            ))}
          </Fragment>
        ))}
      </TableScroll>

      {/* A pager over one page is four dead arrows and a "Page 1 of 1" that answers
          nothing. Same guard the Home table already uses. */}
      {totalPages > 1 && <Pager currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />}
    </Panel>
  );
};
